import { CheckCircle as CheckCircleIcon, Person as PersonIcon } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { userProfileService } from "../../services/UserProfileService";

interface UserInfoStepProps {
  onComplete: (userInfo: { name: string; company: string; jobRole: string }) => void;
}

export const UserInfoStep: React.FC<UserInfoStepProps> = ({ onComplete }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [jobRole, setJobRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const profile = await userProfileService.getUserProfile();

        if (profile) {
          setName(profile.name || "");
          setCompany(profile.company || "");
          setJobRole(profile.jobRole || "");
        }
      } catch (error) {
        console.log("No existing profile found or error fetching:", error);
        // This is expected for new users, so we don't show an error
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleSaveUserInfo = async () => {
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }
    if (!company.trim()) {
      setError("Please enter your company");
      return;
    }
    if (!jobRole.trim()) {
      setError("Please enter your job role");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save user profile to backend
      await userProfileService.updateUserProfile({
        name: name.trim(),
        company: company.trim(),
        jobRole: jobRole.trim(),
      });

      // Pass the data to the parent component
      onComplete({
        name: name.trim(),
        company: company.trim(),
        jobRole: jobRole.trim(),
      });
    } catch (error) {
      console.error("Error saving user info:", error);
      setError("Failed to save user information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching profile
  if (isLoadingProfile) {
    return (
      <Box textAlign="center">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box textAlign="center" mb={4}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            mx: "auto",
            mb: 2,
            bgcolor: "primary.main",
          }}
        >
          <PersonIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" component="h3" gutterBottom fontWeight="medium">
          Tell Us About Yourself
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: "auto" }}>
          Let&apos;s start by getting to know you better. This helps Costaff.ai understand your professional context and provide more relevant assistance.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={3} mb={4}>
        <TextField label="Your Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" fullWidth required helperText="" />

        <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Enter your company name" fullWidth required helperText="The company you work for" />

        <TextField
          label="Your Job Role"
          value={jobRole}
          onChange={(e) => setJobRole(e.target.value)}
          placeholder="e.g., CEO, Marketing Manager, Software Engineer"
          fullWidth
          required
          helperText="Your position or role at the company"
        />
      </Box>

      <Box display="flex" flexDirection="column" gap={2} mb={3}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSaveUserInfo}
          disabled={isLoading || !name.trim()}
          fullWidth
          startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {isLoading ? "Saving..." : "Save & Continue"}
        </Button>
      </Box>
    </Box>
  );
};
